import { Modal } from "./Modal";

// Guía de uso: qué es, cómo funciona y una chuleta de Typst suficiente
// para editar las plantillas sin salir de la página.
export function Guide({ onClose }: { onClose: () => void }) {
  return (
    <Modal ariaLabel="Guía de uso" icon="?" title="guía — cv generator" onClose={onClose}>
      <div className="space-y-5 p-5 text-sm leading-relaxed text-mute">
          <section className="space-y-1.5">
            <h2 className="font-mono text-xs uppercase tracking-widest text-accent"># qué es esto</h2>
            <p>
              Un generador de currículums escrito en{" "}
              <a href="https://typst.app/docs/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                Typst
              </a>
              , un lenguaje de composición tipográfica moderno (piensa en LaTeX, pero
              legible y que compila en milisegundos). Escribes código a la izquierda y
              ves el resultado a la derecha en tiempo real. Cuando estés contento,
              descargas el PDF.
            </p>
            <p>
              Todo se compila <span className="text-ink">dentro de tu navegador</span> (WebAssembly):
              tu CV no se envía a ningún servidor y el borrador se autoguarda solo en
              este dispositivo.
            </p>
          </section>

          <section className="space-y-1.5">
            <h2 className="font-mono text-xs uppercase tracking-widest text-accent"># cómo se usa</h2>
            <ol className="list-decimal space-y-1 pl-5">
              <li>Carga una plantilla con el selector de arriba (clásico, compacto, con foto, mono o ejecutivo).</li>
              <li>Sustituye los datos de ejemplo por los tuyos. La vista se refresca sola al dejar de teclear.</li>
              <li>Si rompes algo, la barra inferior muestra el error y la vista conserva el último render bueno.</li>
              <li>Descarga el PDF final, o el fuente <code className="rounded bg-bg px-1 text-ink">.typ</code> para guardarlo/versionarlo.</li>
            </ol>
          </section>

          <section className="space-y-1.5">
            <h2 className="font-mono text-xs uppercase tracking-widest text-accent"># foto de perfil</h2>
            <p>
              Pulsa <span className="text-ink">«foto»</span> en la barra superior para subir una imagen
              (JPG/PNG). Se recorta y comprime en tu navegador —
              <span className="text-ink"> no se sube a ningún sitio</span> — y queda disponible en las
              plantillas como <code className="rounded bg-bg px-1 text-ink">image("/profile.png")</code>.
              La plantilla <span className="text-ink">«con foto»</span> ya la coloca en una cabecera
              circular; en cualquier otra puedes insertarla donde quieras con esa línea.
            </p>
            <p className="text-[11px]">
              Ojo con los ATS: muchos parsers <span className="text-ink">ignoran las imágenes</span>, así
              que la foto es estética — nunca metas texto (nombre, contacto) dentro de la imagen.
            </p>
          </section>

          <section className="space-y-1.5">
            <h2 className="font-mono text-xs uppercase tracking-widest text-accent"># chuleta typst</h2>
            <pre className="overflow-x-auto rounded border border-line bg-bg p-3 font-mono text-xs leading-relaxed text-ink">
{`== Título de sección          encabezado (nivel 2)
*negrita*  _cursiva_          énfasis
- elemento                    lista con viñetas
+ elemento                    lista numerada
#link("https://x.com")[web]   enlace con texto
#grid(columns: (1fr, auto),   dos columnas: puesto a la
  [*Puesto* — Empresa],        izquierda, fechas a la
  [2023 --- Hoy],              derecha
)
#set text(size: 11pt)         cambia el tamaño base
#set page(margin: 1.5cm)      cambia los márgenes
// comentario                 no sale en el PDF`}
            </pre>
            <p>
              Referencia completa en{" "}
              <a href="https://typst.app/docs/reference/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                typst.app/docs
              </a>
              . Fuentes disponibles: Libertinus Serif, New Computer Modern y DejaVu Sans Mono.
            </p>
          </section>

          <section className="space-y-1.5">
            <h2 className="font-mono text-xs uppercase tracking-widest text-accent"># por qué puntúa bien en los ATS</h2>
            <p>
              Los sistemas de seguimiento de candidaturas (ATS) extraen el texto del PDF
              y lo trocean en campos. Las plantillas están pensadas para ese parseo:
            </p>
            <ul className="list-disc space-y-1 pl-5">
              <li><span className="text-ink">Una sola columna</span> — el orden de lectura nunca se mezcla.</li>
              <li><span className="text-ink">Secciones canónicas</span> (Perfil, Experiencia, Formación, Habilidades) que los parsers reconocen.</li>
              <li><span className="text-ink">Sin tablas, iconos ni gráficos</span> — solo texto real, 100% extraíble y seleccionable.</li>
              <li><span className="text-ink">Contacto en el cuerpo</span>, no en cabeceras/pies que muchos ATS descartan.</li>
              <li>Tipografías estándar y tamaños legibles.</li>
            </ul>
            <p>
              Consejo: copia las palabras clave de la oferta (tecnologías, certificaciones,
              funciones) en tu perfil y experiencia — la puntuación de la mayoría de ATS es,
              en esencia, coincidencia de keywords. Y cuantifica logros: «reduje X un 40%»
              vale más que «responsable de X».
            </p>
            <p>
              Pulsa <span className="text-ink">«✓ ats»</span> en la barra superior para
              analizar tu CV contra estas reglas: te da una nota, avisos y sugerencias
              concretas. El análisis corre <span className="text-ink">entero en tu
              navegador</span> sobre el texto del editor — nada se sube.
            </p>
          </section>

          <section className="space-y-1.5">
            <h2 className="font-mono text-xs uppercase tracking-widest text-accent"># qué más se puede hacer</h2>
            <ul className="list-disc space-y-1 pl-5">
              <li>Cartas de presentación, portadas de informes o cualquier documento — es Typst completo, no solo CVs.</li>
              <li>Varias versiones del CV por oferta: descarga el <code className="rounded bg-bg px-1 text-ink">.typ</code> de cada una y recárgalo pegándolo en el editor.</li>
              <li>Ajustar márgenes, tamaños y espaciados con <code className="rounded bg-bg px-1 text-ink">#set</code> para encajar todo en una página.</li>
            </ul>
          </section>
      </div>
    </Modal>
  );
}
